import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Grid, CircularProgress, Snackbar, Alert, TextField, Button } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

// Example color palette for pie chart
const COLORS = ['#1976d2', '#43a047', '#f6ae2d', '#e15759', '#6d9886', '#9c27b0'];

export default function HotshotAnalyticsDashboard({ token }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [trendData, setTrendData] = useState([]);
  const [jobTypeData, setJobTypeData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        // Fetch hot shot analytics from backend (replace with real endpoint)
        const res = await fetch('/api/analytics/hotshot', { credentials: 'include' });
        const data = await res.json();
        setStats(data.stats);
        setTrendData(data.trendData);
        setJobTypeData(data.jobTypeData);
        setRegionData(data.regionData);
      } catch {
        setStats(null);
        setSnackbar({ open: true, message: 'Failed to load analytics.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const handleDateChange = e => {
    const { name, value } = e.target;
    setDateRange(r => ({ ...r, [name]: value }));
  };
  const handleFilter = () => {
    setSnackbar({ open: true, message: 'Date filter applied (demo only)', severity: 'info' });
  };
  const handleExport = () => {
    setSnackbar({ open: true, message: 'Exported analytics (demo only)', severity: 'success' });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Hot Shot Analytics</Typography>
      <Paper elevation={2} sx={{ mb: 3, p: { xs: 1, sm: 2 }, borderRadius: 3, boxShadow: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, bgcolor: 'background.default' }}>
        <Typography variant="subtitle2" sx={{ mr: 1, color: 'text.secondary', minWidth: 80 }}>Date Range:</Typography>
        <TextField label="From" type="date" name="from" size="small" value={dateRange.from} onChange={handleDateChange} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140, maxWidth: 170, bgcolor: 'background.paper', borderRadius: 2, '& .MuiInputBase-root': { borderRadius: 2 } }} />
        <TextField label="To" type="date" name="to" size="small" value={dateRange.to} onChange={handleDateChange} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140, maxWidth: 170, bgcolor: 'background.paper', borderRadius: 2, '& .MuiInputBase-root': { borderRadius: 2 } }} />
        <Button variant="contained" onClick={handleFilter} sx={{ height: 38, minWidth: 110, fontWeight: 600, fontSize: 15, px: 2, borderRadius: 2, boxShadow: 1 }}>Filter</Button>
        <Button variant="outlined" onClick={handleExport} sx={{ height: 38, minWidth: 110, fontWeight: 600, fontSize: 15, px: 2, borderRadius: 2, boxShadow: 1 }}>Export</Button>
      </Paper>
      {loading ? <CircularProgress /> : stats ? (
        <Grid container columns={12} spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 2, textAlign: 'center', fontWeight: 700 }}>Total Hot Shot Jobs<br /><span style={{ fontSize: 24 }}>{stats.totalJobs}</span></Paper></Grid>
          <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 2, textAlign: 'center', fontWeight: 700 }}>Total Revenue<br /><span style={{ fontSize: 24 }}>${stats.totalRevenue}</span></Paper></Grid>
          <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 2, textAlign: 'center', fontWeight: 700 }}>Avg. Response Time<br /><span style={{ fontSize: 24 }}>{stats.avgResponseTime} min</span></Paper></Grid>
          <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 2, textAlign: 'center', fontWeight: 700 }}>On-Time Delivery %<br /><span style={{ fontSize: 24 }}>{stats.onTimeDeliveryPercent}%</span></Paper></Grid>
        </Grid>
      ) : <Typography color="text.secondary">No analytics data.</Typography>}
      <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary', height: 340, mt: 2, mb: 2, minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, color: 'text.primary' }}>Revenue & Jobs Trend</Typography>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" interval={0} tick={{ fontSize: 12 }} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="revenue" stroke="#1976d2" strokeWidth={2} name="Revenue" />
            <Line type="monotone" dataKey="jobs" stroke="#43a047" strokeWidth={2} name="Jobs" />
          </LineChart>
        </ResponsiveContainer>
      </Paper>
      <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary', height: 340, minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, color: 'text.primary' }}>Jobs by Type</Typography>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie data={jobTypeData} dataKey="value" nameKey="type" cx="50%" cy="50%" outerRadius={90} label>
              {jobTypeData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Paper>
      <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary', height: 340, minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, color: 'text.primary' }}>Jobs by Region</Typography>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={regionData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="region" interval={0} tick={{ fontSize: 12 }} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="jobs" fill="#f6ae2d" name="Jobs" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>
      <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
